import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import { ArrowClockwise } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { HubFunnel } from "./HubFunnel";
import { HubGrid } from "./HubGrid";
import { HubQueues } from "./HubQueues";
import { HubMembers } from "./HubMembers";
import { HubActivity } from "./HubActivity";
import { C, MONO, STAGE_LABELS } from "./hubShared";
import "./commsHub.css";

const sel = { padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(26,30,36,0.15)", background: "#fff", fontSize: 13 };

export default function CommsHubPage() {
  const [tab, setTab] = useState("overview");
  const [partner, setPartner] = useState("");
  const [memberType, setMemberType] = useState("");
  const [ov, setOv] = useState(null);
  const [drill, setDrill] = useState({ stage: "", queue: "", partner: "" });

  const req = useRef(0);
  const load = useCallback(async () => {
    const id = ++req.current;
    try {
      const { data } = await api.get("/comms-hub/overview", { params: { partner, member_type: memberType } });
      if (id === req.current) setOv(data);
    } catch { if (id === req.current) toast.error("Couldn't load the communications hub"); }
  }, [partner, memberType]);
  useEffect(() => { load(); }, [load]);

  const filters = useMemo(() => ({ partner: drill.partner || partner, member_type: memberType, stage: drill.stage, queue: drill.queue }),
    [drill, partner, memberType]);
  const open = drill.stage || drill.queue;
  const qLabel = ov?.queues.find((q) => q.key === drill.queue)?.label;
  const title = [drill.partner || partner || "All partners", drill.stage && `Currently at "${STAGE_LABELS[drill.stage]}"`, qLabel].filter(Boolean).join(" · ");

  return (
    <div data-testid="comms-hub-page" style={{ padding: "32px 36px 60px", background: C.body, minHeight: "100%", color: C.ink }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: C.red, fontFamily: MONO, fontWeight: 700 }}>Communications Hub</div>
          <h1 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 34, margin: "6px 0 0", letterSpacing: "-0.02em" }}>Who is where, and what we've sent them</h1>
          <p style={{ fontSize: 13.5, color: C.slate, marginTop: 6, maxWidth: 680 }}>
            Every member's activation stage and every email they've had, per partner. Click any number to see the people behind it.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select value={partner} onChange={(e) => { setPartner(e.target.value); setDrill({ stage: "", queue: "", partner: "" }); }} style={sel} data-testid="hub-partner-filter">
            <option value="">All partners</option>
            {(ov?.partners || []).map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={memberType} onChange={(e) => setMemberType(e.target.value)} style={sel} data-testid="hub-member-type-filter">
            <option value="">Main members & dependants</option>
            <option value="main">Main members only</option>
            <option value="dependant">Dependants only</option>
          </select>
          <button onClick={load} data-testid="hub-refresh" className="hub-btn" style={{ background: "#fff", border: "1px solid rgba(26,30,36,0.15)", color: C.ink }}><ArrowClockwise size={14} /> Refresh</button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 4, marginTop: 22, borderBottom: "1px solid rgba(26,30,36,0.10)" }}>
        {[["overview", "Overview & drill-down"], ["activity", "Activity log"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`hub-tab-${k}`}
            style={{ padding: "10px 16px", background: "transparent", border: 0, cursor: "pointer", fontSize: 13, fontWeight: 600,
              color: tab === k ? C.ink : C.slate, borderBottom: `2px solid ${tab === k ? C.red : "transparent"}`, marginBottom: -1 }}>{l}</button>
        ))}
      </div>

      {tab === "activity" ? <div style={{ marginTop: 20 }}><HubActivity partner={partner} memberType={memberType} /></div> : !ov ? (
        <div style={{ marginTop: 30, color: C.slate }} data-testid="hub-loading">Loading…</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 28, marginTop: 22 }}>
          <div style={{ fontSize: 13, color: C.slate }} data-testid="hub-total"><b style={{ color: C.ink, fontFamily: MONO }}>{ov.total}</b> active members in view</div>
          <HubFunnel funnel={ov.funnel} total={ov.total} active={!drill.partner ? drill.stage : ""} onPick={(s) => setDrill({ stage: s, queue: "", partner: "" })} />
          <HubQueues queues={ov.queues} active={drill.queue} onPick={(q) => setDrill({ stage: "", queue: q, partner: "" })} />
          {open && <HubMembers filters={filters} title={title} onClear={() => setDrill({ stage: "", queue: "", partner: "" })} onChanged={load} />}
          <HubGrid grid={ov.grid} funnel={ov.funnel} sel={drill} onPick={(p, s) => setDrill({ stage: s, queue: "", partner: p })} />
        </div>
      )}
    </div>
  );
}
